import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Package, ArrowLeft, FolderKanban, CalendarDays, Plus, Check, Loader2, TrendingUp, Copy, Shield, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api, API } from '../App';
import { ASSETS } from '../constants/site';

const STATUS_LABELS = { in_progress: 'IN PROGRESS', review: 'IN REVIEW', done: 'DELIVERED' };
const STATUS_CLS = {
  in_progress: 'text-sky-300 border-sky-500/40',
  review: 'text-amber-300 border-amber-500/40',
  done: 'text-emerald-300 border-emerald-500/40',
};

function ProjectCard({ p, isStaff, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [newMilestone, setNewMilestone] = useState('');

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${p.id}`, body);
      onChanged();
    } catch {
      toast.error('Could not update project.');
    } finally {
      setBusy(false);
    }
  };

  const doneCount = (p.milestones || []).filter((m) => m.done).length;
  const total = (p.milestones || []).length;

  return (
    <div data-testid="project-card" className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg">{p.title}</h3>
          {isStaff && <p className="text-xs text-slate-500 mt-1">{p.customer_email}</p>}
        </div>
        {isStaff ? (
          <select
            data-testid="project-status-select"
            value={p.status}
            disabled={busy}
            onChange={(e) => patch({ status: e.target.value })}
            className="bg-[#0B1528] border border-slate-700 rounded-lg text-xs px-3 py-2 text-slate-200 outline-none"
          >
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : (
          <span className={`font-mono-tech text-[10px] tracking-widest border rounded-full px-4 py-1.5 ${STATUS_CLS[p.status] || ''}`}>
            {STATUS_LABELS[p.status] || p.status}
          </span>
        )}
      </div>
      {total > 0 && (
        <div className="mb-4">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] transition-all duration-700" style={{ width: `${(doneCount / total) * 100}%` }} />
          </div>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-2">{doneCount} / {total} MILESTONES</p>
        </div>
      )}
      <ul className="space-y-2">
        {(p.milestones || []).map((m, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            {isStaff ? (
              <button
                data-testid="milestone-toggle"
                disabled={busy}
                onClick={() => patch({ toggle_milestone: i })}
                className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${m.done ? 'border-amber-400 bg-amber-500/20' : 'border-slate-600 hover:border-amber-500/50'}`}
              >
                {m.done && <Check size={12} className="text-amber-300" />}
              </button>
            ) : (
              <span className={`h-5 w-5 rounded border flex items-center justify-center ${m.done ? 'border-amber-400 bg-amber-500/20' : 'border-slate-700'}`}>
                {m.done && <Check size={12} className="text-amber-300" />}
              </span>
            )}
            <span className={m.done ? 'text-slate-500 line-through' : 'text-slate-300'}>{m.label}</span>
          </li>
        ))}
      </ul>
      {isStaff && (
        <div className="flex gap-2 mt-4">
          <input
            data-testid="milestone-add-input"
            value={newMilestone}
            onChange={(e) => setNewMilestone(e.target.value)}
            placeholder="Add milestone…"
            className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-500/60 placeholder:text-slate-600"
          />
          <button
            data-testid="milestone-add-button"
            disabled={busy || !newMilestone.trim()}
            onClick={() => { patch({ add_milestone: newMilestone.trim() }); setNewMilestone(''); }}
            className="text-xs border border-amber-500/40 text-amber-300 rounded-lg px-4 hover:bg-amber-500/10 transition-colors disabled:opacity-40"
          >
            <Plus size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

const LEAD_STATUSES = ['new', 'contacted', 'quoted', 'closed', 'lost'];

function SalesDashboard() {
  const [stats, setStats] = useState(null);
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    api.get('/sales/my').then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const setLeadStatus = async (kind, id, status) => {
    try {
      await api.patch(`/leads/${kind}/${id}`, { status });
      setStats((s) => ({ ...s, leads: s.leads.map((l) => (l.id === id && l.kind === kind ? { ...l, lead_status: status } : l)) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update the lead.');
    }
  };

  if (!stats) return null;
  const refLink = stats.sales_code ? `${window.location.origin}/?ref=${stats.sales_code}` : '';
  const pct = stats.target_sales > 0 ? Math.min(100, (stats.sales_count / stats.target_sales) * 100) : 0;

  return (
    <div className="mb-12">
      <h2 className="font-mono-tech text-xs tracking-[0.3em] text-emerald-400/90 uppercase mb-5 flex items-center gap-2">
        <TrendingUp size={14} /> My Sales
      </h2>
      <div className="border border-emerald-500/20 rounded-2xl bg-[#0F1C33]/60 p-6">
        {stats.sales_code && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-xs text-slate-500">Your referral link:</span>
            <code data-testid="sales-ref-link" className="font-mono-tech text-xs text-emerald-300 bg-emerald-500/5 border border-emerald-500/25 rounded-lg px-3 py-2">{refLink}</code>
            <button
              data-testid="sales-ref-copy-button"
              onClick={() => { navigator.clipboard.writeText(refLink); toast.success('Referral link copied.'); }}
              className="inline-flex items-center gap-1.5 text-xs border border-slate-600 hover:border-emerald-400/60 text-slate-300 rounded-full px-4 py-2 transition-colors"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-emerald-300">{stats.sales_count}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">SALES MADE</div>
          </div>
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-amber-300">${Number(stats.sales_total).toLocaleString()}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">REVENUE CLOSED</div>
          </div>
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-slate-200">{stats.target_sales || '—'}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">MONTHLY TARGET</div>
          </div>
          <div className="border border-emerald-500/25 rounded-xl bg-emerald-500/5 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-emerald-300" data-testid="commission-earned">
              ${Number(stats.commission_owed).toLocaleString()}
            </div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">
              COMMISSION ({stats.commission_pct || 0}%){stats.commission_paid ? ' · PAID' : ''}
            </div>
          </div>
        </div>
        {stats.target_sales > 0 && (
          <div className="mb-6">
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-2">{stats.sales_count} / {stats.target_sales} TO TARGET</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="text-xs text-slate-500">Commission statement:</span>
          <input
            data-testid="statement-month-input"
            type="month"
            value={stmtMonth}
            onChange={(e) => setStmtMonth(e.target.value)}
            className="bg-[#0B1528]/70 border border-slate-700/60 rounded-lg px-3 py-2 text-xs outline-none [color-scheme:dark]"
          />
          <a
            data-testid="statement-download-button"
            href={`${API}/sales/statement?month=${stmtMonth}`}
            className="inline-flex items-center gap-1.5 text-xs border border-emerald-500/40 text-emerald-300 rounded-full px-4 py-2 hover:bg-emerald-500/10 transition-colors"
          >
            Download PDF
          </a>
        </div>
        {stats.sales.length > 0 && (
          <div className="space-y-2">
            {stats.sales.map((s, i) => (
              <div key={i} className="flex items-center justify-between border border-slate-800 rounded-lg bg-[#0B1528]/70 px-4 py-3">
                <div className="text-sm text-slate-300">{s.package_name}</div>
                <span className="font-mono-tech text-xs text-emerald-300">${Number(s.amount).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
        {stats.sales.length === 0 && (
          <p className="text-xs text-slate-500">No attributed sales yet — share your referral link; every paid checkout through it counts here.</p>
        )}
        {stats.leads && stats.leads.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-800/60">
            <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-3">MY ASSIGNED LEADS</p>
            <div className="space-y-2">
              {stats.leads.map((l, i) => (
                <div key={i} data-testid="sales-lead-row" className="flex flex-wrap items-center justify-between gap-3 border border-slate-800 rounded-lg bg-[#0B1528]/70 px-4 py-3">
                  <div>
                    <div className="text-sm text-slate-200">{l.name} <span className="text-slate-500">· {l.email}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {l.kind === 'booking' ? `Consultation ${l.date} ${l.time} ET` : (l.business || l.message?.slice(0, 60))}
                    </div>
                    {l.ai_summary && (
                      <div className="text-xs text-amber-200/70 mt-1.5 border-l-2 border-amber-500/40 pl-2 whitespace-pre-wrap">{l.ai_summary}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono-tech text-[9px] tracking-widest border rounded-full px-3 py-1 ${l.kind === 'booking' ? 'text-sky-300 border-sky-500/40' : 'text-amber-300 border-amber-500/40'}`}>
                      {l.kind === 'booking' ? 'BOOKING' : 'ENQUIRY'}
                    </span>
                    <select
                      data-testid="lead-status-select"
                      value={l.lead_status || 'new'}
                      onChange={(e) => setLeadStatus(l.kind, l.id, e.target.value)}
                      className="bg-[#0B1528] border border-slate-700 rounded-lg text-[11px] px-2.5 py-1.5 text-slate-200 outline-none"
                    >
                      {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SecuritySection({ hasPassword }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/set-password', { password: form.next, current_password: form.current });
      toast.success(hasPassword ? 'Password updated.' : 'Password set — you can now sign in with email & password too.');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update the password.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600';

  return (
    <div className="mb-12">
      <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
        <Shield size={14} /> Sign-in & Security
      </h2>
      <form onSubmit={submit} className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 space-y-3">
        <p className="text-xs text-slate-500 mb-1">
          {hasPassword
            ? 'Your account works with Google and password sign-in. Change your password below.'
            : 'You signed up with Google. Set a password to also sign in with email & password — same account, both methods.'}
        </p>
        {hasPassword && (
          <input data-testid="current-password-input" type="password" required placeholder="Current password" value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })} className={inputCls} />
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="new-password-input" type="password" required minLength={8} placeholder="New password (8+ chars)" value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} />
          <input data-testid="confirm-password-input" type="password" required minLength={8} placeholder="Confirm new password" value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
        </div>
        <button data-testid="set-password-button" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 rounded-full px-6 py-2.5 text-sm hover:bg-amber-500/10 transition-colors disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {hasPassword ? 'Update password' : 'Link password sign-in'}
        </button>
      </form>
    </div>
  );
}

function OwnerPanel({ onProjectsChanged, refreshKey, isAdmin }) {
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ customer_email: '', title: '', milestones: '' });
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [blockDate, setBlockDate] = useState('');
  const [reviewUrl, setReviewUrl] = useState('');
  const [hourDate, setHourDate] = useState('');
  const [hourBlocked, setHourBlocked] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteForm, setQuoteForm] = useState({ name: '', quote: '', detail: '' });

  const loadQuotes = () => api.get('/settings/testimonials').then((r) => setQuotes(r.data.items || [])).catch(() => {});

  const addQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/testimonials', quoteForm);
      toast.success('Testimonial published to the homepage.');
      setQuoteForm({ name: '', quote: '', detail: '' });
      loadQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add the testimonial.');
    }
  };

  const removeQuote = async (id) => {
    try {
      await api.delete(`/settings/testimonials/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
    } catch {
      toast.error('Could not remove it.');
    }
  };

  useEffect(() => {
    api.get('/bookings').then((r) => setBookings(r.data)).catch(() => {});
    api.get('/blocked-dates').then((r) => setBlocked(r.data.dates)).catch(() => {});
    if (isAdmin) {
      api.get('/settings/review').then((r) => setReviewUrl(r.data.url || '')).catch(() => {});
      loadQuotes();
    }
  }, [refreshKey]);

  useEffect(() => {
    if (!hourDate) return;
    api.get(`/blocked-slots?date=${hourDate}`).then((r) => setHourBlocked(r.data.times)).catch(() => setHourBlocked([]));
  }, [hourDate]);

  const ALL_TIMES = Array.from({ length: 22 }, (_, i) => `${String(9 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
  const fmtT = (t) => { const [h, m] = t.split(':').map(Number); return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };

  const toggleHour = async (t) => {
    try {
      const { data } = await api.post('/blocked-slots', { date: hourDate, time: t });
      setHourBlocked((b) => (data.blocked ? [...b, t].sort() : b.filter((x) => x !== t)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update that hour.');
    }
  };

  const addBlocked = async () => {
    if (!blockDate) return;
    try {
      await api.post('/blocked-dates', { date: blockDate });
      setBlocked((b) => [...new Set([...b, blockDate])].sort());
      setBlockDate('');
      toast.success('Date blocked — no bookings can be made that day.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not block that date.');
    }
  };

  const removeBlocked = async (d) => {
    try {
      await api.delete(`/blocked-dates/${d}`);
      setBlocked((b) => b.filter((x) => x !== d));
    } catch {
      toast.error('Could not unblock that date.');
    }
  };

  const saveReviewUrl = async () => {
    try {
      await api.put('/settings/review', { url: reviewUrl.trim() });
      toast.success(reviewUrl.trim() ? 'Review button is live at gslab.engineer/review.' : 'Review link cleared.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the link.');
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/projects', {
        customer_email: form.customer_email,
        title: form.title,
        milestones: form.milestones.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Project created — the customer sees it on their next sign-in.');
      setForm({ customer_email: '', title: '', milestones: '' });
      onProjectsChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create project.');
    } finally {
      setBusy(false);
    }
  };

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch {
      toast.error('Could not update booking.');
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600';

  return (
    <div className="space-y-10">
      <div>
        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <CalendarDays size={14} /> Consultation Bookings
        </h2>
        {bookings.length === 0 ? (
          <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">No bookings yet — share your /book link.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} data-testid="owner-booking-row" className="flex flex-wrap items-center justify-between gap-3 border border-slate-800 rounded-xl bg-[#0F1C33]/60 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-200">{b.name} <span className="text-slate-500">· {b.email}</span></div>
                  <div className="font-mono-tech text-[11px] text-amber-300/80 mt-1">{b.date} · {b.time} ET</div>
                </div>
                <select
                  data-testid="booking-status-select"
                  value={b.status}
                  onChange={(e) => setBookingStatus(b.id, e.target.value)}
                  className="bg-[#0B1528] border border-slate-700 rounded-lg text-xs px-3 py-2 text-slate-200 outline-none"
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="done">Done</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <CalendarDays size={14} /> Unavailable Dates
        </h2>
        <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
          <div className="flex gap-3 mb-4">
            <input
              data-testid="block-date-input"
              type="date"
              value={blockDate}
              onChange={(e) => setBlockDate(e.target.value)}
              className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors [color-scheme:dark]"
            />
            <button
              data-testid="block-date-button"
              onClick={addBlocked}
              disabled={!blockDate}
              className="text-sm border border-amber-500/40 text-amber-300 rounded-full px-6 hover:bg-amber-500/10 transition-colors disabled:opacity-40"
            >
              Block this day
            </button>
          </div>
          {blocked.length === 0 ? (
            <p className="text-xs text-slate-500">No blocked dates — every weekday is bookable.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {blocked.map((d) => (
                <span key={d} data-testid="blocked-date-chip" className="inline-flex items-center gap-2 font-mono-tech text-xs border border-red-500/30 bg-red-500/5 text-red-300 rounded-full px-4 py-1.5">
                  {d}
                  <button data-testid="blocked-date-remove" onClick={() => removeBlocked(d)} className="hover:text-red-100">✕</button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-slate-800">
            <p className="text-xs text-slate-500 mb-3">Or block specific hours on a day (9:00 AM – 8:00 PM ET):</p>
            <input
              data-testid="block-hours-date-input"
              type="date"
              value={hourDate}
              onChange={(e) => setHourDate(e.target.value)}
              className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors [color-scheme:dark] mb-3"
            />
            {hourDate && (
              <div data-testid="block-hours-grid" className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {ALL_TIMES.map((t) => {
                  const isBlocked = hourBlocked.includes(t);
                  return (
                    <button
                      key={t}
                      data-testid="block-hour-toggle"
                      onClick={() => toggleHour(t)}
                      className={`rounded-lg border py-2 font-mono-tech text-[11px] transition-colors duration-200 ${
                        isBlocked
                          ? 'border-red-500/50 bg-red-500/10 text-red-300 line-through'
                          : 'border-slate-700/60 text-slate-300 hover:border-amber-500/40'
                      }`}
                    >
                      {fmtT(t)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div>
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
            <Package size={14} /> Google Review Link
          </h2>
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
            <p className="text-xs text-slate-500 mb-3">Paste your Google Business Profile review link — the button at gslab.engineer/review goes live instantly.</p>
            <div className="flex gap-3">
              <input
                data-testid="review-url-input"
                type="url"
                placeholder="https://g.page/r/…/review"
                value={reviewUrl}
                onChange={(e) => setReviewUrl(e.target.value)}
                className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600"
              />
              <button
                data-testid="review-url-save-button"
                onClick={saveReviewUrl}
                className="text-sm bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-6 transition-shadow hover:shadow-[0_0_20px_rgba(212,175,55,0.35)]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div>
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
            <Star size={14} /> Homepage Testimonials
          </h2>
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
            <p className="text-xs text-slate-500 mb-4">Real client quotes shown on the homepage review showcase. Empty = section stays hidden.</p>
            {quotes.length > 0 && (
              <div className="space-y-2 mb-5">
                {quotes.map((q) => (
                  <div key={q.id} data-testid="testimonial-row" className="flex items-start justify-between gap-3 border border-slate-800 rounded-lg bg-[#0B1528]/70 px-4 py-3">
                    <div>
                      <div className="text-sm text-slate-200">{q.name} <span className="text-slate-500 text-xs">{q.detail}</span></div>
                      <div className="text-xs text-slate-500 mt-1">“{q.quote.slice(0, 90)}{q.quote.length > 90 ? '…' : ''}”</div>
                    </div>
                    <button data-testid="testimonial-delete-button" onClick={() => removeQuote(q.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">✕</button>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={addQuote} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="testimonial-name-input" required placeholder="Client name — e.g. Marta Alvarez" value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })} className={inputCls} />
                <input data-testid="testimonial-detail-input" placeholder="Detail — e.g. Landscaping, Atlanta" value={quoteForm.detail}
                  onChange={(e) => setQuoteForm({ ...quoteForm, detail: e.target.value })} className={inputCls} />
              </div>
              <textarea data-testid="testimonial-quote-input" required rows={3} placeholder="Their quote, in their words…" value={quoteForm.quote}
                onChange={(e) => setQuoteForm({ ...quoteForm, quote: e.target.value })} className={inputCls} />
              <button data-testid="testimonial-add-button" type="submit"
                className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 rounded-full px-6 py-2.5 text-sm hover:bg-amber-500/10 transition-colors">
                <Plus size={14} /> Publish testimonial
              </button>
            </form>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <FolderKanban size={14} /> Start a Client Project
        </h2>
        <form onSubmit={createProject} className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="project-customer-email-input" required type="email" placeholder="Customer email (their Google sign-in)" value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={inputCls} />
            <input data-testid="project-title-input" required placeholder="Project title — e.g. Alvarez Landscaping site" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          </div>
          <textarea data-testid="project-milestones-input" rows={4} placeholder={'Milestones, one per line:\nDiscovery call\nDesign approved\nBuild complete\nLaunched'} value={form.milestones}
            onChange={(e) => setForm({ ...form, milestones: e.target.value })} className={inputCls} />
          <button data-testid="project-create-button" type="submit" disabled={busy}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-6 py-2.5 text-sm disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create project
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Account() {
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const effectiveUser = user || location.state?.user;
  const role = effectiveUser?.role || (effectiveUser?.is_owner ? 'admin' : 'customer');
  const isAdmin = role === 'admin';
  const isStaff = isAdmin || role === 'developer';
  const isSales = isAdmin || role === 'sales';

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (effectiveUser) {
      api.get('/payments/my').then((r) => setPayments(r.data)).catch(() => {});
      api.get('/projects').then((r) => setProjects(r.data)).catch(() => {});
    }
  }, [effectiveUser, refreshKey]);

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <div className="font-mono-tech text-amber-400/80 text-sm tracking-widest animate-pulse">VERIFYING SESSION…</div>
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Client Portal</h1>
          <p className="text-sm text-slate-400 mb-8">Sign in with your Google account to view your GSLAB projects and active plans.</p>
          <button data-testid="account-login-button" onClick={login}
            className="w-full bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5">
            Sign in with Google
          </button>
          <a href="/login" className="block text-xs text-slate-400 hover:text-amber-300 mt-4 transition-colors">
            or sign in with email &amp; password
          </a>
          <a href="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mt-6 transition-colors">
            <ArrowLeft size={13} /> Back to gslab.engineer
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-dashboard" className="min-h-screen bg-[#050B14] pt-28 pb-20 px-4 sm:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {effectiveUser.picture ? (
              <img src={effectiveUser.picture} alt="" className="h-14 w-14 rounded-full border border-amber-500/40" />
            ) : (
              <span className="h-14 w-14 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 font-bold text-xl">
                {effectiveUser.name?.[0] || 'U'}
              </span>
            )}
            <div>
              <h1 className="font-display font-bold text-2xl">{effectiveUser.name}</h1>
              <p className="text-sm text-slate-500">
                {effectiveUser.email} · <span className="text-amber-300 uppercase font-mono-tech text-[10px] tracking-widest">{role}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/admin" data-testid="account-admin-link" className="inline-flex items-center gap-2 text-sm text-amber-300 border border-amber-500/40 rounded-full px-4 py-2 hover:bg-amber-500/10 transition-colors">
                <Shield size={14} /> Admin
              </Link>
            )}
            <a href="/" className="text-sm text-slate-400 hover:text-amber-300 transition-colors">← Site</a>
            <button data-testid="account-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 border border-slate-700 rounded-full px-4 py-2 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </motion.div>

        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <Package size={14} /> Your Plans & Purchases
        </h2>
        {payments.length === 0 ? (
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-10 text-center mb-12">
            <p className="text-slate-400 text-sm mb-6">No active plans yet. Pick a build or care plan to get your engine started.</p>
            <a href="/#pricing" data-testid="account-view-pricing-link" className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-7 py-3 text-sm">
              View Pricing
            </a>
          </div>
        ) : (
          <div className="space-y-3 mb-12">
            {payments.map((p) => (
              <div key={p.session_id} className="flex items-center justify-between border border-slate-800 rounded-2xl bg-[#0F1C33]/60 px-6 py-5">
                <div>
                  <div className="font-display font-semibold">{p.package_name}</div>
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-1 uppercase">
                    {p.kind === 'subscription' ? 'MONTHLY PLAN' : 'ONE-TIME'} · ${Number(p.amount).toLocaleString()}
                  </div>
                </div>
                <span className="font-mono-tech text-[10px] tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-4 py-1.5">ACTIVE</span>
              </div>
            ))}
          </div>
        )}

        {isSales && <SalesDashboard />}

        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <FolderKanban size={14} /> {isStaff ? 'Client Projects' : 'Your Projects'}
        </h2>
        {projects.length === 0 ? (
          <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 mb-12">
            {isStaff ? 'No client projects yet — create one below.' : 'No projects yet — once your build kicks off, milestones will appear here.'}
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} isStaff={isStaff} onChanged={refresh} />
            ))}
          </div>
        )}

        {isStaff && <OwnerPanel onProjectsChanged={refresh} refreshKey={refreshKey} isAdmin={isAdmin} />}

        <SecuritySection hasPassword={!!effectiveUser.has_password} />
      </div>
    </div>
  );
}
